import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { TrustBadge } from "@/components/TrustBadge";
import { imgSrc, primaryImage, catLabel, fmtMoney } from "@/lib/api";
import { ImageOff } from "lucide-react";

export const ModelCard = ({ model, index = 0 }) => {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const img = imgSrc(primaryImage(model));

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 10 }}
      animate={reduce ? false : { opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.4), ease: [0.22, 1, 0.36, 1] }}
    >
      <button
        data-testid="model-card"
        onClick={() => navigate(`/models/${model.id}`)}
        className="group block w-full overflow-hidden rounded-xl border bg-card text-left shadow-[0_1px_0_rgba(30,26,22,0.06)] transition-[box-shadow] hover:shadow-[0_10px_30px_rgba(30,26,22,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="relative aspect-[3/4] overflow-hidden bg-muted">
          {img ? (
            <img
              src={img}
              alt={model.name}
              loading="lazy"
              className="h-full w-full object-cover transition-[transform,filter] duration-300 ease-out group-hover:scale-[1.03] group-hover:saturate-[1.05]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <ImageOff className="h-8 w-8" />
            </div>
          )}
          {model.trust && model.trust !== "unverified" && (
            <div className="absolute left-2 top-2">
              <TrustBadge trust={model.trust} className="shadow-sm backdrop-blur-sm" />
            </div>
          )}
        </div>
        <div className="space-y-1.5 p-3 sm:p-4">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-display text-lg leading-tight">{model.name}</h3>
            {model.features?.height_cm && (
              <span className="font-mono-plex text-xs text-muted-foreground">{model.features.height_cm}cm</span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(model.categories || []).slice(0, 3).map((c) => (
              <Badge key={c} variant="secondary" className="rounded-full text-[10px] uppercase tracking-wide">
                {catLabel(c)}
              </Badge>
            ))}
          </div>
          {(model.rate_min || model.rate_max) && (
            <p className="text-xs text-muted-foreground">
              From {fmtMoney(model.rate_min)} <span className="opacity-60">/ licence</span>
            </p>
          )}
        </div>
      </button>
    </motion.div>
  );
};
